/**
 * Sticky mobile bar: swaps between the PIN prompt and the delivery
 * confirmation, keeps the body reserve matched to the rendered height, and
 * gets out of the way while the customer scrolls down.
 */
import { getStoredPin, isDelhiNcrPin } from '@theme/ffp-delivery-checker';

const HIDE_AFTER_PX = 200;
const SCROLL_JITTER_PX = 6;

const bar = document.getElementById('ffp-mobile-bar');

if (bar) {
  const chip = bar.querySelector('[data-ffp-pin-chip]');
  const changeButton = bar.querySelector('[data-ffp-pin-change]');
  const input = bar.querySelector('[data-ffp-pin-input]');

  /**
   * The reserve has to track the real height: the prompt, the confirmation, and
   * the out-of-area warning are all different sizes. Above 749px the bar is
   * display:none, so offsetHeight is 0 and the reserve collapses on its own.
   */
  const syncHeight = () => {
    const height = bar.offsetHeight;
    document.body.style.setProperty('--ffp-mobile-bar-height', `${height}px`);
    const chromeHeight = bar.dataset.hidden === 'true' ? 0 : height;
    document.body.style.setProperty('--ffp-bottom-chrome-height', `${chromeHeight}px`);
  };

  const syncState = () => {
    const pin = getStoredPin();
    if (pin && isDelhiNcrPin(pin)) {
      bar.dataset.state = 'confirmed';
      if (chip) chip.textContent = `Next-day delivery to ${pin}`;
    } else {
      bar.dataset.state = 'prompt';
    }
    syncHeight();
  };

  const reveal = () => bar.removeAttribute('data-hidden');

  changeButton?.addEventListener('click', () => {
    bar.dataset.state = 'prompt';
    syncHeight();
    input?.focus();
    input?.select();
  });

  document.addEventListener('ffp:pin-updated', syncState);

  bar.addEventListener('focusin', reveal);

  let lastY = window.scrollY;
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(() => {
      ticking = false;
      const y = window.scrollY;
      const delta = y - lastY;
      if (Math.abs(delta) < SCROLL_JITTER_PX) return;
      lastY = y;

      // Never slide away from someone typing their PIN.
      if (bar.contains(document.activeElement)) {
        reveal();
        return;
      }

      if (delta > 0 && y > HIDE_AFTER_PX) {
        bar.dataset.hidden = 'true';
      } else if (delta < 0) {
        reveal();
      }
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });

  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(syncHeight).observe(bar);
  } else {
    window.addEventListener('resize', syncHeight, { passive: true });
  }

  syncState();
}
